import { useState, useCallback } from 'react';
import { apiClient } from '@/services/apiClient';
import {
  VideoItem,
  IconikPreviewRequest,
  IconikPreviewResponse,
  UploadRequest,
  UploadResponse,
  CSVRow,
  CSVValidationResult,
  UseVideoSourcesReturn
} from '../types/video';

const ICONIK_PREVIEW_ENDPOINT = '/console/asset_management/iconik/preview';
const UPLOAD_ENDPOINT = '/console/asset_management/impact/upload';

// CSV parsing utility
const parseCSV = (content: string): CSVValidationResult => {
  // Remove BOM if present
  const cleanContent = content.replace(/^\uFEFF/, '');
  const lines = cleanContent.trim().split('\n');
  const headers = lines[0]?.toLowerCase().split(',').map(h => h.trim());
  
  if (!headers || headers.length !== 2) {
    throw new Error('CSV must have exactly 2 columns: village_id, iconik_url');
  }
  
  const villageIdIndex = headers.findIndex(h => h === 'village_id');
  const iconikUrlIndex = headers.findIndex(h => h === 'iconik_url');
  
  if (villageIdIndex === -1 || iconikUrlIndex === -1) {
    throw new Error('CSV must contain columns: village_id, iconik_url');
  }
  
  const valid: CSVRow[] = [];
  const invalid: Array<{ row: number; data: any; errors: string[]; }> = [];
  
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',').map(v => v.trim());
    const errors: string[] = [];
    
    const village_id = values[villageIdIndex]?.trim();
    const iconik_url = values[iconikUrlIndex]?.trim();
    
    if (!village_id) {
      errors.push('village_id is required');
    }
    
    if (!iconik_url) {
      errors.push('iconik_url is required');
    } else if (!iconik_url.includes('iconik.io') && !iconik_url.includes('icnk.io')) {
      errors.push('iconik_url must be a valid Iconik URL (iconik.io or icnk.io)');
    }
    
    if (errors.length > 0) {
      invalid.push({
        row: i + 1,
        data: values,
        errors
      });
    } else {
      valid.push({ village_id, iconik_url });
    }
  }
  
  return { valid, invalid };
};

export const useVideoSources = (): UseVideoSourcesReturn => {
  const [videos, setVideos] = useState<VideoItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Add videos from CSV file
  const addFromCSV = useCallback(async (file: File) => {
    try {
      setLoading(true);
      setError(null);
      
      const content = await file.text();
      const { valid, invalid } = parseCSV(content);
      
      if (invalid.length > 0) {
        console.warn('CSV validation errors:', invalid);
        // Could show warnings to user about invalid rows
      }
      
      const newVideos: VideoItem[] = valid.map(row => ({
        village_id: row.village_id,
        iconik_url: row.iconik_url,
        metadata: {
          title: '',
          description: '',
          program: '',
          event: '',
          asset_type: 'Video'
        },
        status: 'pending'
      }));
      
      setVideos(prev => [...prev, ...newVideos]);
      
      if (invalid.length > 0) {
        setError(`${valid.length} videos added successfully. ${invalid.length} rows had errors and were skipped.`);
      }
    } catch (err) {
      console.error('Error processing CSV:', err);
      setError(err instanceof Error ? err.message : 'Failed to process CSV file');
    } finally {
      setLoading(false);
    }
  }, []);

  // Add single video manually
  const addManual = useCallback((village_id: string, iconik_url: string) => {
    const newVideo: VideoItem = {
      village_id,
      iconik_url,
      metadata: {
        title: '',
        description: '',
        program: '',
        event: '',
        asset_type: 'Video'
      },
      status: 'pending'
    };
    
    setVideos(prev => [...prev, newVideo]);
  }, []);

  // Remove video
  const removeVideo = useCallback((index: number) => {
    setVideos(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Preview videos (call iconik preview API)
  const previewVideos = useCallback(async (indices?: number[]) => {
    const targetIndices = indices || videos.map((_, i) => i);
    const targetVideos = targetIndices.map(i => videos[i]).filter(v => v.status === 'pending');
    
    if (targetVideos.length === 0) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Update status to previewing
      setVideos(prev => prev.map((video, i) => {
        if (targetIndices.includes(i) && video.status === 'pending') {
          return { ...video, status: 'previewing' as const };
        }
        return video;
      }));
      
      // Prepare preview request
      const previewRequest: IconikPreviewRequest = {
        assets: targetVideos.map(video => ({
          village_id: video.village_id,
          iconik_url: video.iconik_url
        }))
      };
      
      console.log('Calling iconik preview API:', previewRequest);
      
      const response = await apiClient.post<IconikPreviewResponse>(
        ICONIK_PREVIEW_ENDPOINT,
        previewRequest
      );
      
      if (response.data?.data) {
        // Update videos with preview data
        setVideos(prev => {
          const updated = [...prev];
          
          targetVideos.forEach((video, targetIndex) => {
            const actualIndex = targetIndices[targetIndex];
            const previewResult = response.data.data[targetIndex];
            
            if (previewResult.success && previewResult.url) {
              updated[actualIndex] = {
                ...video,
                preview: previewResult,
                metadata: {
                  ...video.metadata,
                  title: video.metadata.title || previewResult.filename.replace(/\.[^/.]+$/, ""), // Remove extension
                  description: video.metadata.description || `Video from ${previewResult.filename}`
                },
                status: 'ready'
              };
            } else {
              updated[actualIndex] = {
                ...video,
                status: 'error',
                error: previewResult.error || 'Failed to generate preview'
              };
            }
          });
          
          return updated;
        });
      }
    } catch (err: any) {
      console.error('Error previewing videos:', err);
      console.error('API response:', err.response?.data);
      console.error('API status:', err.response?.status);
      
      let errorMessage = 'Failed to preview videos';
      if (err.response?.status === 404) {
        errorMessage = 'Preview API endpoint not found. Check API configuration.';
      } else if (err.response?.status === 403) {
        errorMessage = 'Insufficient permissions. ICONIK.VIEWER role required for preview.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      
      // Reset status for failed previews
      setVideos(prev => prev.map((video, i) => {
        if (targetIndices.includes(i) && video.status === 'previewing') {
          return { ...video, status: 'error', error: 'Preview failed' };
        }
        return video;
      }));
    } finally {
      setLoading(false);
    }
  }, [videos]);

  // Update metadata for a video
  const updateMetadata = useCallback((index: number, metadata: Partial<VideoItem['metadata']>) => {
    setVideos(prev => prev.map((video, i) => {
      if (i === index) {
        return {
          ...video,
          metadata: { ...video.metadata, ...metadata }
        };
      }
      return video;
    }));
  }, []);

  // Upload videos to CDN
  const uploadVideos = useCallback(async (indices?: number[]) => {
    const targetIndices = indices || videos.map((_, i) => i);
    const targetVideos = targetIndices
      .map(i => ({ video: videos[i], index: i }))
      .filter(({ video }) => video.status === 'ready' && video.preview);
    
    if (targetVideos.length === 0) {
      setError('No ready videos to upload');
      return;
    }
    
    // Validate required fields before upload
    const invalidVideos = targetVideos.filter(({ video }) => {
      return !video.metadata.title || !video.metadata.program || !video.preview?.asset_id;
    });
    
    if (invalidVideos.length > 0) {
      setError('Some videos are missing required fields (title, program). Please edit metadata before uploading.');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Update status to uploading
      setVideos(prev => prev.map((video, i) => {
        if (targetIndices.includes(i) && video.status === 'ready') {
          return { ...video, status: 'uploading' as const };
        }
        return video;
      }));
      
      // Prepare upload request according to API documentation
      const uploadRequest: UploadRequest = {
        assets: targetVideos.map(({ video }) => ({
          id: video.preview!.asset_id,
          title: video.metadata.title.trim(),
          description: video.metadata.description?.trim() || '',
          type: 'Video',
          source: 'Iconik',
          filename: video.preview!.filename,
          fileUrl: video.preview!.url!, // Use signed URL from preview
          village_id: video.village_id,
          program: video.metadata.program,
          asset_type: video.metadata.asset_type || 'Video',
          event: video.metadata.event || '',
          date: video.preview!.captured_at
        }))
      };
      
      console.log('Calling upload API:', uploadRequest);
      
      const response = await apiClient.post<UploadResponse>(
        UPLOAD_ENDPOINT,
        uploadRequest
      );
      
      if (response.data?.data) {
        const { success, failed } = response.data.data;
        
        // Update video statuses based on upload results
        setVideos(prev => prev.map((video, i) => {
          if (targetIndices.includes(i) && video.status === 'uploading') {
            if (success.find(s => s.id === video.preview?.asset_id)) {
              return { ...video, status: 'uploaded' };
            } else if (failed.find(f => f.id === video.preview?.asset_id)) {
              const failedItem = failed.find(f => f.id === video.preview?.asset_id);
              return { 
                ...video, 
                status: 'error',
                error: failedItem?.error || 'Upload failed'
              };
            }
          }
          return video;
        }));
        
        if (failed.length > 0) {
          setError(`${success.length} videos uploaded successfully. ${failed.length} failed.`);
        }
      }
    } catch (err: any) {
      console.error('Error uploading videos:', err);
      
      // Handle specific API errors
      let errorMessage = 'Failed to upload videos';
      if (err.response?.status === 404) {
        errorMessage = 'Upload API is not available in this environment. Video upload only works in production.';
      } else if (err.response?.status === 403) {
        errorMessage = 'Insufficient permissions. IMPACT_ASSET.MODERATOR role required for video upload.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      
      // Reset status for failed uploads
      setVideos(prev => prev.map((video, i) => {
        if (targetIndices.includes(i) && video.status === 'uploading') {
          return { 
            ...video, 
            status: 'error', 
            error: errorMessage.includes('production') ? 'Upload not available in this environment' : 'Upload failed' 
          };
        }
        return video;
      }));
    } finally {
      setLoading(false);
    }
  }, [videos]);

  // Clear all videos
  const clearAll = useCallback(() => {
    setVideos([]);
    setError(null);
  }, []);

  return {
    videos,
    loading,
    error,
    addFromCSV,
    addManual,
    removeVideo,
    previewVideos,
    updateMetadata,
    uploadVideos,
    clearAll
  };
};